<script setup lang="ts">
const props = defineProps<{
  reviewData
}>()
const { t } = useI18n()
const review = ref(props.reviewData)

const yellowStars = ref(Math.floor(review.value.rating))
const grayStars = ref(5 - yellowStars.value)
//

const profilePicturePath = ref('')

if (review.value.gender === 'woman') {
  profilePicturePath.value = `/webcontent/uploads/w/_${review.value.profile_picture}`
}
else if (review.value.gender === 'man') {
  profilePicturePath.value = `/webcontent/uploads/m/_${review.value.profile_picture}`
}
</script>

<template>
  <div class="w-full rounded-lg bg-gray-50 p-6">
    <div class="flex items-center justify-between">
      <div class="flex items-center">
        <NuxtImg
          :alt="review.name" class="mr-4 h-10 w-10 rounded-full" height="40"
          :src="profilePicturePath"
          width="40"
        />
        <div>
          <h2 class="text-gray-800 font-medium">
            {{ review.name }}
          </h2>
          <p class="text-sm text-gray-500">
            {{ t('review.rated', [review.date]) }}
          </p>
        </div>
      </div>
      <div class="flex items-center">
        <div v-if="yellowStars > 0" class="text-yellow-400">
          <template v-for="star in yellowStars" :key="star">
            <Icon name="i-heroicons:star-16-solid" />
          </template>
        </div>
        <div v-if="grayStars > 0" class="text-gray-300">
          <template v-for="star in grayStars" :key="star">
            <Icon name="i-heroicons:star-16-solid" />
          </template>
        </div>
      </div>
    </div>
    <div class="mt-4 flex gap-x-4">
      <ul v-if="review.pros">
        <li v-for="(pro, index) in review.pros" :key="index">
          <span
            class="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs text-green-800 font-medium"
          >
            <svg class="mr-1.5 h-2 w-2 text-green-400" fill="currentColor" viewbox="0 0 8 8">
              <circle cx="4" cy="4" r="3" />
            </svg>
            {{ pro }}
          </span>
        </li>
      </ul>
      <ul v-if="review.cons">
        <li v-for="(con, index) in review.cons" :key="index">
          <span
            class="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs text-red-800 font-medium"
          >
            <svg class="mr-1.5 h-2 w-2 text-red-400" fill="currentColor" viewbox="0 0 8 8">
              <circle cx="4" cy="4" r="3" />
            </svg>
            {{ con }}
          </span>
        </li>
      </ul>
    </div>
    <div class="mt-4 flex items-center justify-between">
      <span class="flex items-center text-sm text-green-600 font-medium">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" class="mr1" viewBox="0 0 24 24"><path fill="currentColor" fill-rule="evenodd" d="M9.592 3.2a5.574 5.574 0 0 1-.495.399c-.298.2-.633.338-.985.408c-.153.03-.313.043-.632.068c-.801.064-1.202.096-1.536.214a2.713 2.713 0 0 0-1.655 1.655c-.118.334-.15.735-.214 1.536a5.707 5.707 0 0 1-.068.632c-.07.352-.208.687-.408.985c-.087.13-.191.252-.399.495c-.521.612-.782.918-.935 1.238c-.353.74-.353 1.6 0 2.34c.153.32.414.626.935 1.238c.208.243.312.365.399.495c.2.298.338.633.408.985c.03.153.043.313.068.632c.064.801.096 1.202.214 1.536a2.713 2.713 0 0 0 1.655 1.655c.334.118.735.15 1.536.214c.319.025.479.038.632.068c.352.07.687.209.985.408c.13.087.252.191.495.399c.612.521.918.782 1.238.935c.74.353 1.6.353 2.34 0c.32-.153.626-.414 1.238-.935c.243-.208.365-.312.495-.399c.298-.2.633-.338.985-.408c.153-.03.313-.043.632-.068c.801-.064 1.202-.096 1.536-.214a2.713 2.713 0 0 0 1.655-1.655c.118-.334.15-.735.214-1.536c.025-.319.038-.479.068-.632c.07-.352.209-.687.408-.985c.087-.13.191-.252.399-.495c.521-.612.782-.918.935-1.238c.353-.74.353-1.6 0-2.34c-.153-.32-.414-.626-.935-1.238a5.574 5.574 0 0 1-.399-.495a2.713 2.713 0 0 1-.408-.985a5.72 5.72 0 0 1-.068-.632c-.064-.801-.096-1.202-.214-1.536a2.713 2.713 0 0 0-1.655-1.655c-.334-.118-.735-.15-1.536-.214a5.707 5.707 0 0 1-.632-.068a2.713 2.713 0 0 1-.985-.408a5.73 5.73 0 0 1-.495-.399c-.612-.521-.918-.782-1.238-.935a2.713 2.713 0 0 0-2.34 0c-.32.153-.626.414-1.238.935m6.781 6.663a.814.814 0 0 0-1.15-1.15l-4.85 4.85l-1.596-1.595a.814.814 0 0 0-1.15 1.15l2.17 2.17a.814.814 0 0 0 1.15 0z" clip-rule="evenodd" /></svg>        {{ t('review.verified_purchase') }}</span>
      <div class="flex overflow-hidden space-x-2">
        <template v-if="review.product_images">
          <NImageModal v-for="(image, index) in review.product_images" :key="index" :image="`/webcontent/uploads/products/${image}`">
            <NuxtImg :src="`/webcontent/uploads/products/${image}`" :alt="image" class="object-fit h-14 w-14 rounded-md object-cover ring-2 ring-white" height="64" width="64" />
          </NImageModal>
        </template>
      </div>
    </div>
  </div>
</template>
